// Date handling for pedigree data-overlay criteria (TGCLOUD-1670).
//
// Which reader a field needs is decided by HOW IT IS WRITTEN, never by its
// declared Mongoose type. That rule cost six defects the last time it was
// guessed (TGCLOUD-1631/1632/1633 and TGCLOUD-1707), and every one of them was
// invisible at UTC, which is what CI runs. So the anchoring is declared here,
// field by field, and the evaluator may not read a date any other way.
//
//   picker AND importer -> mixed          -> toLocalDateFromPickerOrImport
//   server timestamp    -> true instant   -> toLocalDateFromInstant
//   toStoredDate        -> UTC midnight   -> toLocalDate
//   type: String date   -> calendar date  -> toLocalDate
//
// Every clinical-record date a clinician picks is ALSO written by the
// spreadsheet importers, as UTC midnight of the calendar day (TGCLOUD-1751
// batch writer survey). Reading those with toLocalDateFromInstant shows an
// imported date a day early at a NEGATIVE offset; reading the picker's "…Z"
// value with toLocalDate shows it a day early at a POSITIVE one. So they are
// all 'mixed'. Person dob and dod ARE toStoredDate strings, and server
// timestamps are true instants.

import { startOfDay, addDays, differenceInCalendarDays } from 'date-fns';
import {
  toLocalDate,
  toLocalDateFromInstant,
  toLocalDateFromPickerOrImport
} from 'src/util/partialDate';

/** Server-written timestamps: true instants, read by their local parts. */
const INSTANT_FIELDS = Object.freeze([
  'createdAt',
  'updatedAt',
  'latestAt',
  'assignedAt'
]);

/**
 * Clinical-record dates written by a picker (a local-anchored "…Z" instant)
 * AND by importers (UTC midnight) with nothing recording which wrote a value.
 */
const MIXED_FIELDS = Object.freeze([
  'clinicalNoteDate',
  'dateRecorded',
  'dateReported',
  'appointmentDate',
  'contactDate',
  'assignedDate',
  'dateApproved',
  'reminderDate',
  'referralDate',
  'dateClosed',
  'consentRequestDate',
  'consentDate',
  'dateTaken',
  'statusDate',
  'dateRequested',
  'dateResultDue',
  'dateResultReceived',
  'treatmentDate',
  'treatmentEndDate',
  'pathReportDate',
  'reportRequestDate',
  'reportReceivedDate',
  'blocksRequestedDate',
  'blocksReceivedDate',
  'blocksReturnedDate',
  'processStartDate',
  'endDate'
]);

/** Calendar-date strings: toStoredDate output and questionnaire dates. */
const CALENDAR_FIELDS = Object.freeze(['dob', 'dod', 'consent.date']);

export const DATE_ANCHORING = Object.freeze({
  ...Object.fromEntries(INSTANT_FIELDS.map((f) => [f, 'instant'])),
  ...Object.fromEntries(MIXED_FIELDS.map((f) => [f, 'mixed'])),
  ...Object.fromEntries(CALENDAR_FIELDS.map((f) => [f, 'calendar']))
});

/** True when the field name is one this module knows how to read as a date. */
export const isDateField = (field) =>
  Object.prototype.hasOwnProperty.call(DATE_ANCHORING, field);

/**
 * Read a stored value as a local calendar Date, using the declared anchoring.
 * An unknown field falls back to the mixed reader: every clinician-picked
 * `type: Date` column in this codebase is also written by an importer, and
 * the mixed reader is correct for both writers.
 */
export function readDate(field, value) {
  if (value == null || value === '') return null;
  const anchoring = DATE_ANCHORING[field];
  if (anchoring === 'calendar') return toLocalDate(value);
  if (anchoring === 'instant') return toLocalDateFromInstant(value);
  return toLocalDateFromPickerOrImport(value);
}

/**
 * Resolve a criteria value to a comparable calendar day.
 *
 * Accepts an absolute 'YYYY-MM-DD' string, a Date, or a relative token
 * `{ rel: 'today', offsetDays?: number }`. Only `today` is supported, so there
 * is no time-of-day ambiguity to get wrong.
 */
export function resolveDateValue(value, asOf) {
  if (value == null) return null;
  if (value instanceof Date) return startOfDay(value);
  if (typeof value === 'object' && value.rel === 'today') {
    const base = startOfDay(asOf);
    const offset = Number(value.offsetDays) || 0;
    return offset ? addDays(base, offset) : base;
  }
  const parsed = toLocalDate(value);
  return parsed ? startOfDay(parsed) : null;
}

/** Whole days between two calendar days. Positive means `date` is in the past. */
export function daysSince(date, asOf) {
  if (!date) return null;
  return differenceInCalendarDays(startOfDay(asOf), startOfDay(date));
}

/** Whole days until `date`. Negative means it has passed. */
export function daysUntil(date, asOf) {
  const since = daysSince(date, asOf);
  return since == null ? null : -since;
}

/** Compare two calendar days, tolerant of nulls (a null never matches). */
export function compareDays(a, b) {
  if (!a || !b) return null;
  return differenceInCalendarDays(startOfDay(a), startOfDay(b));
}

/** Age in whole years at `asOf`, from a UTC-anchored dob string. */
export function ageAt(dob, asOf) {
  const born = readDate('dob', dob);
  if (!born) return null;
  const ref = startOfDay(asOf);
  let age = ref.getFullYear() - born.getFullYear();
  const monthDiff = ref.getMonth() - born.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && ref.getDate() < born.getDate())) {
    age -= 1;
  }
  return age >= 0 ? age : null;
}

/** The local calendar day, stamped into a result so the frame is explicit. */
export function stampAsOf(asOf) {
  const d = startOfDay(asOf);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
